/**
 * Calculates the Probability Density Function (PDF) for a logistic distribution.
 * @param x The value on the x-axis.
 * @returns The probability density at point x.
 */
const logisticPDF = (x: number) => {
  const ex = Math.exp(-x)
  const denominator = Math.pow(1 + ex, 2)
  return ex / denominator
}

/**
 * The Cumulative Distribution Function (CDF) for the standard logistic distribution.
 * It gives the probability that a random variable is less than or equal to x.
 */
const logisticCDF = (x: number) => {
  return 1 / (1 + Math.exp(-x))
}

/**
 * Generates an array of {x, y} points for the logistic distribution curve.
 * @param loc The location parameter.
 * @param scale The scale parameter.
 * @param lbound - Lower bound parameter.
 * @param ubound - Upper bound parameter.
 * @returns An array of points.
 */
export const logisticSample = (loc: number, scale: number, lbound?: number, ubound?: number) => {
  const points = []

  // Determine the range for point generation, relative to loc and scale
  const step = 0.1
  const genStart = -12 * scale + loc
  const genEnd = 12 * scale + loc

  let scalingFactor = 1.0

  // If the distribution is truncated, calculate the scaling factor.
  if (lbound !== undefined || ubound !== undefined) {
    // Use -Infinity for an undefined lower bound and +Infinity for an undefined upper bound.
    const effectiveLBound = lbound ?? -Infinity
    const effectiveUBound = ubound ?? Infinity

    // Standardize the bounds
    const stdLBound = (effectiveLBound - loc) / scale
    const stdUBound = (effectiveUBound - loc) / scale

    // The total probability mass within the original (non-truncated) distribution's bounds
    const totalMass = logisticCDF(stdUBound) - logisticCDF(stdLBound)

    // The scaling factor is 1 divided by the total mass.
    // This redistributes the "lost" probability from outside the bounds
    // to the points inside the bounds.
    if (totalMass > 0) {
      scalingFactor = 1 / totalMass
    } else {
      // If there is no probability mass between the bounds, no points can be generated.
      return []
    }
  }

  for (let x = genStart; x <= genEnd; x += step) {
    // If lbound is defined and x is below it, skip.
    if (lbound !== undefined && x < lbound) {
      continue
    }
    // If ubound is defined and x is above it, skip.
    if (ubound !== undefined && x > ubound) {
      continue
    }

    const pdfValue = logisticPDF((x - loc) / scale) / scale
    // Apply the scaling factor to adjust probability for truncation
    const y = pdfValue * scalingFactor

    points.push({ x, y })
  }

  return points
}
